import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';

import { MODERATION_REASONS_LIST_PAGE_ROUTE } from 'utils/constants/app';

import { useStaticDataProvider } from '@hh.ru/magritte-common-data-provider';
import {
    ActionBar,
    BottomSheet,
    Button,
    CheckableChip,
    ChipsContainer,
    ChipsInput,
    ChipsTriggerState,
    Link,
    Modal,
    NavigationBar,
    Text,
    TextArea,
    useBreakpoint,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { rejectBanner } from 'components/Admin/ModerationList/CampaignModeration/Banner/RejectBannerModal/rejectBanner';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { AdvertisingServiceType, RejectReasonAvailabilityType } from 'store/models/moderation';
import { useDispatch, useSelector } from 'store/utils';

interface RejectBannerModalProps {
    visible: boolean;
    onClose: () => void;
    selectedBanner: { bannerId: number; campaignId: number; advertisingServiceType: AdvertisingServiceType } | null;
}

interface RejectFormState {
    selectedReasons: number[];
    comment: string;
}

const TrlKeys = {
    title: 'admin.moderation.banner.rejectBannerModal.title',
    buttons: {
        reject: 'admin.moderation.banner.rejectBannerModal.buttons.reject',
        cancel: 'admin.moderation.banner.rejectBannerModal.buttons.cancel',
        apply: 'admin.moderation.banner.rejectBannerModal.buttons.apply',
    },
    reasonsList: 'admin.moderation.banner.rejectBannerModal.reasonsList',
    postfix: 'admin.moderation.banner.rejectBannerModal.postfix',
    label: 'admin.moderation.banner.rejectBannerModal.label',
    commentPlaceholder: 'admin.moderation.banner.rejectBannerModal.comment.placeholder',
    validationError: 'admin.moderation.banner.rejectBannerModal.validationError',
    searchInputPlaceholder: 'admin.moderation.banner.rejectBannerModal.searchInput.placeholder',
};

const TOP_REASONS_IDS = ['46', '15', '9', '42', '21', '12', '11', '27'];

export const RejectBannerModal: FC<RejectBannerModalProps> = ({ visible, onClose, selectedBanner }) => {
    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();

    const { bannerId, campaignId, advertisingServiceType } = selectedBanner || {};
    const isInformationBanner = advertisingServiceType === AdvertisingServiceType.Information;

    const moderationReasons = useSelector((state) => state.moderation.moderationReasons);

    const selectOptions = useMemo(
        () =>
            moderationReasons
                .filter(
                    ({ rejectReasonAvailabilityType }) =>
                        rejectReasonAvailabilityType === RejectReasonAvailabilityType.All ||
                        (isInformationBanner
                            ? rejectReasonAvailabilityType === RejectReasonAvailabilityType.Information
                            : rejectReasonAvailabilityType === RejectReasonAvailabilityType.Advertisement)
                )
                .sort((a, b) => a.title.localeCompare(b.title))
                .map((reason) => ({ text: reason.title, value: reason.id.toString() })),
        [moderationReasons, isInformationBanner]
    );

    const [formState, setFormState] = useState<RejectFormState>({
        selectedReasons: [],
        comment: '',
    });

    const [showError, setShowError] = useState(false);

    const [chipsValue, setChipsValue] = useState<ChipsTriggerState>({
        value: '',
        chips: [],
    });
    const staticProvider = useStaticDataProvider(selectOptions);

    useEffect(() => {
        const reasonsText = formState.selectedReasons
            .map((reasonId) => {
                const reason = moderationReasons.find((r) => r.id === reasonId);
                return reason?.defaultComment || '';
            })
            .filter(Boolean)
            .join('\n');
        setFormState((prev) => ({ ...prev, comment: reasonsText }));
    }, [formState.selectedReasons, moderationReasons]);

    const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFormState((prev) => ({
            ...prev,
            comment: e.target.value,
        }));
    };

    const handleReasonsChange = (newValue: ChipsTriggerState) => {
        setChipsValue(newValue);
        setShowError(false);
        const newSelectedReasons = newValue.chips.map((item) => Number(item.value));
        setFormState((prev) => ({
            ...prev,
            selectedReasons: newSelectedReasons,
        }));
    };

    const topReasonHandleChange = (value: ChangeEvent<HTMLInputElement>) => {
        const reasonId = value.target.value;
        const reason = moderationReasons.find(({ id }) => id === Number(reasonId));
        if (reason) {
            if (formState.selectedReasons.includes(Number(reasonId))) {
                handleReasonsChange({
                    ...chipsValue,
                    chips: chipsValue.chips.filter(({ value }) => value !== reasonId),
                });
            } else {
                handleReasonsChange({
                    ...chipsValue,
                    chips: [...chipsValue.chips, { value: reasonId, label: reason.title }],
                });
            }
        }
    };

    const content = (
        <VSpacingContainer default={24}>
            <ChipsInput
                type="select"
                state={chipsValue}
                onChange={handleReasonsChange}
                triggerProps={{
                    placeholder: trls(TrlKeys.label),
                    maxHeight: 100,
                    size: 'medium',
                    disabled: false,
                    invalid: showError,
                    errorMessage: showError && trls(TrlKeys.validationError),
                }}
                selectProps={{
                    type: 'checkbox',
                    name: 'reasons',
                    dataProvider: staticProvider,
                    bottomSheetHeight: 'full-screen',
                    applyChangesButton: (
                        <Button mode="primary" size="medium" style="accent">
                            {trls(TrlKeys.buttons.apply)}
                        </Button>
                    ),
                    plain: true,
                }}
            />
            <ChipsContainer>
                {TOP_REASONS_IDS.filter((id) => !!selectOptions.find(({ value }) => value === id)).map((id) => (
                    <CheckableChip
                        key={id}
                        value={id}
                        checked={formState.selectedReasons.includes(Number(id))}
                        name="top-reason"
                        type="checkbox"
                        onChange={topReasonHandleChange}
                        data-qa={`moderation-reject-reason-chip-${id}`}
                    >
                        {moderationReasons.find((r) => r.id === Number(id))?.title || ''}
                    </CheckableChip>
                ))}
            </ChipsContainer>
            <TextArea
                value={formState.comment}
                onChange={handleCommentChange}
                name="comment"
                layout="hug"
                placeholder={trls(TrlKeys.commentPlaceholder)}
            />
        </VSpacingContainer>
    );

    const action = <CrossOutlinedSize24 initialColor="secondary" onClick={onClose} />;

    const footer = (
        <ActionBar
            primaryActions={
                <>
                    <Button mode="secondary" style="accent" data-qa="moderation-reject-modal-cancel" onClick={onClose}>
                        {trls(TrlKeys.buttons.cancel)}
                    </Button>
                    <Button
                        mode="primary"
                        style="accent"
                        data-qa="moderation-reject-modal-confirm"
                        onClick={() => {
                            if (formState.selectedReasons.length === 0) {
                                setShowError(true);
                                return;
                            }
                            bannerId &&
                                campaignId &&
                                dispatch(
                                    rejectBanner(bannerId, campaignId, formState.selectedReasons, formState.comment)
                                );
                        }}
                    >
                        {trls(TrlKeys.buttons.reject)}
                    </Button>
                </>
            }
        />
    );

    return (
        <>
            {!isMobile ? (
                <Modal
                    size="large"
                    position="right"
                    visible={visible}
                    onClose={onClose}
                    header={
                        <MagritteModalHeader
                            title={trls(TrlKeys.title)}
                            subtitle={
                                <Link inline href={MODERATION_REASONS_LIST_PAGE_ROUTE} target="_blank">
                                    <Text typography="paragraph-1-regular">{trls(TrlKeys.reasonsList)}</Text>
                                </Link>
                            }
                            actions={action}
                        />
                    }
                    footer={footer}
                >
                    {content}
                </Modal>
            ) : (
                <BottomSheet
                    visible={visible}
                    onClose={onClose}
                    footer={footer}
                    height="full-screen"
                    header={
                        <NavigationBar
                            title={trls(TrlKeys.title)}
                            subtitle={
                                <Link inline href={MODERATION_REASONS_LIST_PAGE_ROUTE} target="_blank">
                                    {trls(TrlKeys.reasonsList)}
                                </Link>
                            }
                            right={action}
                        />
                    }
                >
                    {content}
                </BottomSheet>
            )}
        </>
    );
};
